import { GradientValue, GradientStop } from "./gradientTypes";
import { sortStops } from "./gradientUtils";

export interface ViewBox {
  width: number;
  height: number;
}

const stopToSvg = (stop: GradientStop): string => {
  const opacity = stop.opacity / 100;
  return `    <stop offset="${stop.position}%" stop-color="${stop.color}" stop-opacity="${opacity}"/>`;
};

/**
 * Convert a CSS angle (degrees) to SVG linearGradient x1/y1/x2/y2 coordinates.
 * CSS: 0deg = bottom-to-top, 90deg = left-to-right
 */
const angleToSvgCoords = (
  angleDeg: number
): { x1: string; y1: string; x2: string; y2: string } => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  const x1 = Math.round(50 - Math.cos(rad) * 50);
  const y1 = Math.round(50 - Math.sin(rad) * 50);
  const x2 = Math.round(50 + Math.cos(rad) * 50);
  const y2 = Math.round(50 + Math.sin(rad) * 50);
  return {
    x1: `${x1}%`,
    y1: `${y1}%`,
    x2: `${x2}%`,
    y2: `${y2}%`
  };
};

export const createSvgLinearGradientDef = (
  gradient: GradientValue & { type: "linear" },
  id: string,
  viewBox?: ViewBox
): string => {
  const stopsStr = sortStops(gradient.stops)
    .map(stopToSvg)
    .join("\n");

  if (viewBox) {
    const coords = angleToSvgCoords(gradient.angle);
    const x1 = Math.round((parseFloat(coords.x1) / 100) * viewBox.width);
    const y1 = Math.round((parseFloat(coords.y1) / 100) * viewBox.height);
    const x2 = Math.round((parseFloat(coords.x2) / 100) * viewBox.width);
    const y2 = Math.round((parseFloat(coords.y2) / 100) * viewBox.height);
    return `  <linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}">
${stopsStr}
  </linearGradient>`;
  }

  const coords = angleToSvgCoords(gradient.angle);
  return `  <linearGradient id="${id}" x1="${coords.x1}" y1="${coords.y1}" x2="${coords.x2}" y2="${coords.y2}">
${stopsStr}
  </linearGradient>`;
};

export const createSvgRadialGradientDef = (
  gradient: GradientValue & { type: "radial" },
  id: string,
  viewBox?: ViewBox
): string => {
  const stopsStr = sortStops(gradient.stops)
    .map(stopToSvg)
    .join("\n");

  if (viewBox) {
    const cx = Math.round((gradient.centerX / 100) * viewBox.width);
    const cy = Math.round((gradient.centerY / 100) * viewBox.height);
    const r = Math.round(Math.max(viewBox.width, viewBox.height) / 2);
    return `  <radialGradient id="${id}" gradientUnits="userSpaceOnUse" cx="${cx}" cy="${cy}" r="${r}">
${stopsStr}
  </radialGradient>`;
  }

  return `  <radialGradient id="${id}" cx="${gradient.centerX}%" cy="${gradient.centerY}%" r="50%">
${stopsStr}
  </radialGradient>`;
};

export const createSvgGradientDef = (
  gradient: GradientValue,
  id: string,
  viewBox?: ViewBox
): string => {
  if (gradient.type === "linear") {
    return createSvgLinearGradientDef(gradient, id, viewBox);
  }
  return createSvgRadialGradientDef(gradient, id, viewBox);
};

export const wrapInDefs = (gradientDefs: string[]): string => {
  if (gradientDefs.length === 0) return "";
  return `<defs>\n${gradientDefs.join("\n")}\n</defs>`;
};
